import { createSignal, type JSX, onCleanup, onMount, Show } from "solid-js";

/**
 * Utility component to defer rendering of heavy content.
 * By waiting for a couple of animation frames or a specific delay in milliseconds,
 * we allow parent entrance animations to start and finish smoothly before the main
 * thread gets busy rendering children.
 *
 * Best used inside Drawers, Modals, or complex Transitions.
 */
const DEFAULT_DEFER_FRAMES = 2;

interface DeferredProps {
  children: JSX.Element;
  frames?: number;
  delayMs?: number;
}

export function Deferred(props: DeferredProps) {
  const [show, setShow] = createSignal(false);

  onMount(() => {
    if (props.delayMs !== undefined) {
      const timer = setTimeout(() => setShow(true), props.delayMs);
      onCleanup(() => {
        clearTimeout(timer);
      });
      return;
    }

    const frames = props.frames ?? DEFAULT_DEFER_FRAMES;
    let count = 0;
    let animId = 0;

    const wait = () => {
      if (count >= frames) {
        setShow(true);
      } else {
        count += 1;
        animId = requestAnimationFrame(wait);
      }
    };

    animId = requestAnimationFrame(wait);
    onCleanup(() => {
      cancelAnimationFrame(animId);
    });
  });

  return <Show when={show()}>{props.children}</Show>;
}
